import React from "react";
import { useState, useEffect } from "react";
import DateHelpers from "../Helper/DateHelpers";
import '../Styles/PersonalInfo.scss';
import '../Styles/FatherSonPersonalInfo.scss';
import InputValidation from "../Helper/InputValidation";
import { getRetreatConfig, getSonCount, getPerSonFee } from "../Constants/RetreatConfig";

// Personal info form for the Father and Son Adventure — adds an "Add Son" row for listing
// the sons attending with the father. Reuses PersonalInfo.scss so the card/heading/button/
// input styling is pixel-identical to the standard PersonalInfo form.
function FatherSonPersonalInfo({ week, personalInformation, setPersonalInformation, selectedPage, setSelectedPage, previousPage, nextPage }) {

   // Father and Son Adventure only admits sons, so the gender radio buttons are pointless
   // here — the gender is locked and defaulted instead of asked for.
   const fixedGender = getRetreatConfig(week).fixedGender

   useEffect(() => {
      if (fixedGender && personalInformation.gender !== fixedGender) {
         setPersonalInformation({ ...personalInformation, gender: fixedGender })
      }
   }, [fixedGender])

   const [invalidGenderError, setInvalidGenderError] = useState(false)
   const [invalidFirstNameError, setInvalidFirstNameError] = useState(false)
   const [invalidLastNameError, setInvalidLastNameError] = useState(false)
   const [invalidStreetAddress, setInvalidStreetAddress] = useState(false)
   const [invalidCity, setInvalidCity] = useState(false)
   const [invalidState, setInvalidState] = useState(false)
   const [invalidZip, setInvalidZip] = useState(false)
   const [invalidZipFormat, setInvalidZipFormat] = useState(false)
   const [invalidPhone, setInvalidPhone] = useState(false)
   const [invalidPhoneFormat, setInvalidPhoneFormat] = useState(false)
   const [invalidBirthDate, setInvalidBirthDate] = useState(false)
   const [invalidECName, setInvalidECName] = useState(false)
   const [invalidECRelationship, setInvalidECRelationship] = useState(false)
   const [invalidECPhone, setInvalidECPhone] = useState(false)
   const [invalidECPhoneFormat, setInvalidECPhoneFormat] = useState(false)

   const sonNames = personalInformation.sonNames && personalInformation.sonNames.length > 0
      ? personalInformation.sonNames
      : ['']

   // Each son is charged the full price of the event, so the running cost is shown here rather
   // than being discovered for the first time on the review page. Counted and priced with the
   // same helpers the charge uses, so the figure on screen can never disagree with what gets
   // billed. The registration/camp split is spelled out because the father sees those two figures
   // as separate line items on the review page — showing only the $45 total invites the question.
   const perSonFee = getPerSonFee(week)
   const perSonRegistrationFee = Number(week?.registration_fee ?? 0)
   const perSonCampFee = Number(week?.camp_fee ?? 0)
   const billableSonCount = getSonCount(personalInformation)

   function CalculateMinimumDate() {
      let minimumDate = DateHelpers.subtractFromDate(new Date(), 100)
      let formattedDate = DateHelpers.getFormattedDate(minimumDate)
      return formattedDate;
   }

   function handleSonNameChange(index, value) {
      const updatedSonNames = [...sonNames]
      updatedSonNames[index] = value
      setPersonalInformation({ ...personalInformation, sonNames: updatedSonNames })
   }

   function handleAddSon() {
      setPersonalInformation({ ...personalInformation, sonNames: [...sonNames, ''] })
   }

   function ValidatePersonalInformation() {
      let isError = false;
      if (personalInformation.gender === "") {
         setInvalidGenderError(true)
         isError = true;
      }
      if (personalInformation.firstName === "") {
         setInvalidFirstNameError(true)
         isError = true
      }
      if (personalInformation.lastName === "") {
         setInvalidLastNameError(true)
         isError = true
      }
      if (personalInformation.strAddress === "") {
         setInvalidStreetAddress(true)
         isError = true
      }
      if (personalInformation.city === "") {
         setInvalidCity(true)
         isError = true
      }
      if (personalInformation.state === "") {
         setInvalidState(true)
         isError = true
      }
      if (personalInformation.zip === "") {
         setInvalidZip(true)
         isError = true
      }
      if (personalInformation.zip != "") {
         const condition = /^(?!00000)\d{5}(?:[-\s](?!0000)\d{4})?$/
         if (condition.test(personalInformation.zip) === false) {
            setInvalidZipFormat(true)
            isError = true
         }
      }
      if (personalInformation.phone === "") {
         setInvalidPhone(true)
         isError = true
      }
      if (personalInformation.phone != "") {
         const condition = /^\d{3}[ -]?\d{3}[ -]?\d{4}$/
         if (condition.test(personalInformation.phone) === false) {
            setInvalidPhoneFormat(true)
            isError = true
         }
      }
      if (personalInformation.birthDate === "") {
         setInvalidBirthDate(true)
         isError = true
      }
      if (personalInformation.ecName === "") {
         setInvalidECName(true)
         isError = true
      }
      if (personalInformation.ecRelationship === "") {
         setInvalidECRelationship(true)
         isError = true
      }
      if (personalInformation.ecPhone === "") {
         setInvalidECPhone(true)
         isError = true
      }
      if (personalInformation.ecPhone != "") {
         const condition = /^\d{3}[ -]?\d{3}[ -]?\d{4}$/
         if (condition.test(personalInformation.ecPhone) === false) {
            setInvalidECPhoneFormat(true)
            isError = true
         }
      }
      if (isError === false) {
         setSelectedPage(nextPage)
      }
   }

   return (
      <div className='personalInfoContent'>
         <form>
            <h2 id='personalInfoTitle'>Personal Info</h2>
            {!fixedGender &&
               <div className="camperGender">
                  {invalidGenderError === true &&
                     <div className="genderError">
                        * Gender is required
                     </div>
                  }
                  <div className='gender'>
                     <div className="MaleLabel">
                        <label id="male">Male</label>
                     </div>
                     <div className="Male">
                        <input
                           type="radio"
                           value="M"
                           name="fav_language"
                           checked={personalInformation.gender === "M"}
                           onChange={(e) => {
                              setPersonalInformation({ ...personalInformation, gender: (e.target.value) })
                              setInvalidGenderError(false)
                           }}
                        />
                     </div>
                     <div className='FemaleLabel'>
                        <label className="female">Female</label>
                     </div>

                     <div className="Female">
                        <input
                           type="radio"
                           value="F"
                           name="fav_language"
                           checked={personalInformation.gender === "F"}
                           onChange={(e) => {
                              setPersonalInformation({ ...personalInformation, gender: (e.target.value) })
                              setInvalidGenderError(false)
                           }}
                        />
                     </div>
                  </div>
               </div>
            }

            <div className='camperName'>
               <div className="firstName">
                  {invalidFirstNameError === true &&
                     <div className="firstNameError">
                        * First name is required
                     </div>
                  }
                  <label>First Name:* </label>
                  <input
                     type="text"
                     required
                     value={personalInformation.firstName}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, firstName: (e.target.value) });
                        setInvalidFirstNameError(false)
                     }}
                  />
               </div>

               <div className="lastName">
                  {invalidLastNameError === true &&
                     <div className="lastNameError">
                        * Last name is required
                     </div>
                  }
                  <label>Last Name:* </label>
                  <input
                     type="text"
                     required
                     value={personalInformation.lastName}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, lastName: (e.target.value) });
                        setInvalidLastNameError(false)
                     }}
                  />
               </div>
            </div>

            <div className="sonNamesRow">
               <label className="sonNamesRowLabel">
                  Add Son(s):{perSonFee > 0 && ` (+$${perSonFee.toFixed(2)} each)`}
               </label>
               {perSonFee > 0 &&
                  <div className="sonNamesNote">
                     Each son added is charged the full price of the retreat &mdash;
                     &nbsp;${perSonRegistrationFee.toFixed(2)} registration fee
                     &nbsp;+&nbsp;${perSonCampFee.toFixed(2)} camp fee
                     &nbsp;=&nbsp;${perSonFee.toFixed(2)} per son, added to your total.
                  </div>
               }
               {sonNames.map((sonName, index) => (
                  <div className="sonNamesInputRow" key={index}>
                     <input
                        type="text"
                        value={sonName}
                        onChange={(e) => handleSonNameChange(index, e.target.value)}
                     />
                     {index === sonNames.length - 1 &&
                        <button
                           type="button"
                           className="sonNamesAddButton"
                           onClick={handleAddSon}
                           aria-label="Add another son"
                        >
                           +
                        </button>
                     }
                  </div>
               ))}
               {perSonFee > 0 && billableSonCount > 0 &&
                  <div className="sonNamesSubtotal">
                     {billableSonCount} {billableSonCount === 1 ? 'son' : 'sons'} &times; ${perSonFee.toFixed(2)}
                     &nbsp;=&nbsp;+${(billableSonCount * perSonFee).toFixed(2)}
                  </div>
               }
            </div>

            <div className='streetCity'>
               <div className="addressInput">
                  {invalidStreetAddress === true &&
                     <div className="streetAddressError">
                        * Address is required
                     </div>
                  }
                  <label>Street Address:* </label>
                  <input
                     type="text"
                     required
                     value={personalInformation.strAddress}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, strAddress: (e.target.value) })
                        setInvalidStreetAddress(false)
                     }}
                  />
               </div>
               <div className="addressInput">
                  {invalidCity === true &&
                     <div className="cityError">
                        * City is required
                     </div>
                  }
                  <label>City:* </label>
                  <input
                     type="text"
                     required
                     value={personalInformation.city}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, city: (e.target.value) })
                        setInvalidCity(false)
                     }}
                  />
               </div>
            </div>
            <div className="stateZip">
               <div className="stateInput">
                  {invalidState === true &&
                     <div className="stateError">
                        * State is required
                     </div>
                  }
                  <label for="state">State: </label>
                  <select
                     value={personalInformation.state}
                     defaultValue={personalInformation.state}
                     onChange={event => {
                        setPersonalInformation({ ...personalInformation, state: (event.target.value) })
                        setInvalidState(false)
                     }}
                     id="state"
                     name="state">
                     <option value="---">---</option>
                     <option value="Alabama">Alabama</option>
                     <option value="Alaska">Alaska</option>
                     <option value="Arizona">Arizona</option>
                     <option value="Arkansas">Arkansas</option>
                     <option value="California">California</option>
                     <option value="Colorado">Colorado</option>
                     <option value="Connecticut">Connecticut</option>
                     <option value="Delaware">Delaware</option>
                     <option value="District of Columbia">District of Columbia</option>
                     <option value="Florida">Florida</option>
                     <option value="Georgia">Georgia</option>
                     <option value="Guam">Guam</option>
                     <option value="Hawaii">Hawaii</option>
                     <option value="Idaho">Idaho</option>
                     <option value="Illinois">Illinois</option>
                     <option value="Indiana">Indiana</option>
                     <option value="Iowa">Iowa</option>
                     <option value="Kansas">Kansas</option>
                     <option value="Kentucky">Kentucky</option>
                     <option value="Louisiana">Louisiana</option>
                     <option value="Maine">Maine</option>
                     <option value="Maryland">Maryland</option>
                     <option value="Massachusetts">Massachusetts</option>
                     <option value="Michigan">Michigan</option>
                     <option value="Minnesota">Minnesota</option>
                     <option value="Mississippi">Mississippi</option>
                     <option value="Missouri">Missouri</option>
                     <option value="Montana">Montana</option>
                     <option value="Nebraska">Nebraska</option>
                     <option value="Nevada">Nevada</option>
                     <option value="New Hampshire">New Hampshire</option>
                     <option value="New Jersey">New Jersey</option>
                     <option value="New Mexico">New Mexico</option>
                     <option value="New York">New York</option>
                     <option value="North Carolina">North Carolina</option>
                     <option value="North Dakota">North Dakota</option>
                     <option value="Northern Marianas Islands">Northern Marianas Islands</option>
                     <option value="Ohio">Ohio</option><option value="Oklahoma">Oklahoma</option>
                     <option value="Oregon">Oregon</option>
                     <option value="Pennsylvania">Pennsylvania</option>
                     <option value="Puerto Rico">Puerto Rico</option>
                     <option value="Rhode Island">Rhode Island</option>
                     <option value="South Carolina">South Carolina</option>
                     <option value="South Dakota">South Dakota</option>
                     <option value="Tennessee">Tennessee</option>
                     <option value="Texas">Texas</option>
                     <option value="Utah">Utah</option>
                     <option value="Vermont">Vermont</option>
                     <option value="Virginia">Virginia</option>
                     <option value="Virgin Islands">Virgin Islands</option>
                     <option value="Washington">Washington</option>
                     <option value="West Virginia">West Virginia</option>
                     <option value="Wisconsin">Wisconsin</option>
                     <option value="Wyoming">Wyoming</option>
                  </select>
               </div>
               <div className="zipInput">
                  {invalidZip === true &&
                     <div className="zipError">
                        * Zip code is required
                     </div>
                  }
                  {invalidZipFormat === true &&
                     <div className="zipFormatError">
                        * Zip code must be formatted as such: 12345, 12345 6789, or 12345-6789
                     </div>
                  }
                  <label>Zip:* </label>
                  <input
                     type="text"
                     required
                     maxLength={10}
                     value={personalInformation.zip}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, zip: InputValidation.ValidateInputForPhoneAndZipCode(e.target.value) })
                        setInvalidZip(false)
                        setInvalidZipFormat(false)
                     }} />
               </div>
            </div>
            <div className='camperContact'>
               <div className="contactInput">
                  {invalidPhone === true &&
                     <div className="phoneError">
                        * Phone number is required
                     </div>
                  }
                  {invalidPhoneFormat === true &&
                     <div className="phoneFormatError">
                        * Phone number must be formatted as such: 1234567890, 123 456 7890, or 123-456-7890
                     </div>
                  }
                  <label>Cell Phone #:* </label>
                  <input
                     type="text"
                     required
                     maxLength={12}
                     value={personalInformation.phone}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, phone: InputValidation.ValidateInputForPhoneAndZipCode(e.target.value) })
                        setInvalidPhone(false)
                        setInvalidPhoneFormat(false)
                     }} />
               </div>
               <div className="dateOfBirthInput">
                  {invalidBirthDate === true &&
                     <div className="birthDateError">
                        * Birth date is required
                     </div>
                  }
                  <label>Birthdate :*</label>
                  <input
                     type="date"
                     required
                     min={CalculateMinimumDate()}
                     value={personalInformation.birthDate}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, birthDate: (e.target.value) })
                        setInvalidBirthDate(false)
                     }} />
               </div>
            </div>
            <hr></hr>
            <h4>Emergency Contact Info</h4>
            <div className='camperEmergencyContact'>

               <div className="ecInput">
                  {invalidECName === true &&
                     <div className="ecNameError">
                        * Contact name is required
                     </div>
                  }
                  <label>Name (First Last): * </label>
                  <input
                     type="text"
                     required
                     value={personalInformation.ecName}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, ecName: (e.target.value) })
                        setInvalidECName(false)
                     }} />
               </div>

               <div className="ecInput">
                  {invalidECRelationship === true &&
                     <div className="ecRelationshipError">
                        * Contact relationship is required
                     </div>
                  }
                  <label>Relationship:* </label>
                  <input
                     type="text"
                     required
                     value={personalInformation.ecRelationship}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, ecRelationship: (e.target.value) })
                        setInvalidECRelationship(false)
                     }} />
               </div>

               <div className="ecInput">
                  {invalidECPhone === true &&
                     <div className="ecPhoneError">
                        * Contact phone number is required
                     </div>
                  }
                  {invalidECPhoneFormat === true &&
                     <div className="ecPhoneFormatError">
                        * Phone number must be formatted as such: 1234567890, 123 456 7890, or 123-456-7890
                     </div>
                  }
                  <label>Phone Number:* </label>
                  <input
                     type="tel"
                     id="phone"
                     name="phone"
                     pattern="[0-9]{3}-[0-9]{2}-[0-9]{3}"
                     maxLength={12}
                     required
                     value={personalInformation.ecPhone}
                     onChange={(e) => {
                        setPersonalInformation({ ...personalInformation, ecPhone: InputValidation.ValidateInputForPhoneAndZipCode(e.target.value) })
                        setInvalidECPhone(false)
                        setInvalidECPhoneFormat(false)
                     }}
                  />
               </div>
            </div>

            <div className="disclaimerPI">
               <>* Fields are required</>
            </div>
         </form>
         {(
            invalidFirstNameError === true  || invalidLastNameError === true ||
            invalidCity === true            || invalidState === true         ||
            invalidZip === true             || invalidZipFormat === true     ||
            invalidPhone === true           || invalidPhoneFormat === true   ||
            invalidBirthDate === true       || invalidECName === true        ||
            invalidECRelationship === true  || invalidECPhone === true       ||
            invalidECPhoneFormat === true) &&
            <div className="personalInfoErrorMessage">
               Please correct the above error(s):
            </div>
         }
         <div className="personalInfoButtonsContainer">
            <div className="personalInfoButtons">
               <div className="personalInfoPreviousButton">
                  <button className='prevButton' onClick={() => setSelectedPage(previousPage)}>
                     Previous
                  </button>
               </div>
               <div className="personalInfoNextButton">
                  <button className="nextButton" onClick={() => ValidatePersonalInformation()}>
                     Next
                  </button>
               </div>
            </div>
         </div>
      </div>

   )
}
export default FatherSonPersonalInfo;
